'use client';

// Аккаунты: подключённые сервисы (креды n8n) + «+ Подключить» (Этап 8).
// Drawer-компоненты вынесены: connection-forms.tsx (подключение/карточка аккаунта).
// Наших эндпоинтов (Scriptera AI / Scriptera Run) в списке нет — владелец убрал их
// 2026-08-01 («есть и есть»): раздел показывает только доступы клиента.
//
// Устройство раздела — как у «Продуктов» (владелец 2026-08-03): в колонке только
// категории и вход в каталог, подключённые доступы — ПЛИТКАМИ справа по категориям
// (/app/connections/cat/<слаг>), карточка доступа и форма подключения из каталога —
// МОДАЛКОЙ (useSlidePanel, нижний лист; сохранение — ✓ в шапке, submitRef).
import { accountsFast } from '@/lib/bot-live-fast';
import { useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { openNativeSettingsPage } from '@/lib/account-native';
import { usePersistentData } from '@/lib/use-persistent-data';
import { IgQueueNote } from './ig-queue-note';
import { useLocale, useTranslations } from 'next-intl';
import { useSlidePanel } from './native-layer-host';
import { HERO_ICONS, openHeroAsk } from './hero-ask-sheet';
import { readLayoutMode, useLayoutMode, webOnly, readPhoneLook, usePhoneLook } from './layout-mode';
import { SettingsHero } from './settings-hero';
import { IconConnections } from './icons';
import { KButton, KListGroup, KListRow } from '@scriptera/ui/kit';
import { isOwnPanelHref, panelAddress, useAddressPick } from './panel-address';
import { sectionUrl } from '@/lib/cabinet-urls';
import {
  PageAction,
  PageAddButton,
  PaneActSlotContext,
  PaneActiveContext,
  type SectionActionRef,
} from './page-action';
import { DEmpty } from './drawer-kit';
import { FilterButton, FilterRow } from './sheet-filter';
import { Sk } from './skeleton';
import { LoadError } from './load-error';
import { isCustomServiceCredType } from '@scriptera/ui/scenario';
import { PICKER_GROUPS, fallbackServiceName, serviceFor } from './connection-catalog';
import {
  ConnectPicker,
  ConnectCredTypeForm,
  AccountDrawer,
  CONN_PAGES,
  ServiceLogo,
  nativeConnectSupported,
  warmAccountData,
  warmCredSchema,
  serviceNameOf,
  useConnectFeed,
  useNativeConnectPages,
  useNicheAsk,
  useAccountOffHint,
  ACCOUNTS_KEY,
  ACCOUNTS_URL,
  type Account,
  type AccountsData,
  accountUrl,
} from './connection-forms';
import { CustomServiceForm } from './custom-service-form';
import { PopMenu } from '@scriptera/ui';
import { DOTS } from './card-tools';
import { askConfirm } from './ask';
import type { ConnectionsPick } from './split-menu';
import { readRoleHex } from '@/lib/native-app';
import type {
  NativeAccountField,
  NativeAccountGroup,
  NativeAccountHero,
  NativeAccountPage,
} from '@/lib/native-app';

/**
 * ПОДПИСЬ ПЛИТКИ ДОСТУПА — ОПОЗНАВАТЕЛЬ, А НЕ ПОВТОР НАЗВАНИЯ КАНАЛА (владелец 29.08:
 * «сверху написано, что за канал, есть логотип канала — а в названии ты пишешь его
 * снова; пиши просто логин»).
 *
 * При подключении доступ получает имя вида «Instagram @mmtru» — оно нужно внутри (по нему
 * узнаются двойники и уведённые учётки), но человеку в плитке читалось трижды: логотип,
 * заголовок «Instagram» и подпись «„Instagram @mmtru“ · @mmtru». Поэтому имя, СОБРАННОЕ
 * НАМИ (название сервиса + логин), подписью не показывается вовсе — показывается логин.
 * Имя, которое человек дал сам, показываем как прежде: переименование обязано быть видно.
 */
/**
 * ПЛИТКИ ПОДКЛЮЧЕНИЙ В ПОЛЕ ШИРОКОГО ЭКРАНА (TARIFF-PAGE-TZ п.6 / §11 П6, владелец 11.10.2026: «плитки как у ботов
 * с большей информацией»). Та же карточка, что у бота (`.scn-card`: плитка сети, заголовок, подстроки, «⋯»);
 * подстроки — состояние, «В ботах: …», «Переписок: N», «Подключено: дата». На телефоне остаются строки, как в
 * iOS-приложении (кадр K10) — плитки только там, где телефонного экрана нет.
 */
function ConnTiles({
  rows,
  icons,
  openAccount,
  reload,
}: {
  rows: Account[];
  /** Значки сервисов из каталога подключений (общий свод страницы). */
  icons: Map<string, string | undefined>;
  openAccount: (a: Account) => void;
  reload: () => void;
}) {
  const t = useTranslations('cabinet.connections');
  const locale = useLocale();
  // QUICK-START §11.32 (владелец 29.10: «в списке аккаунтов тумблеры убери, оставь только в открытом аккаунте внутри»):
  // выключатель — только внутри открытого аккаунта (connection-forms.tsx); на плитке — подпись состояния и точка.
  const offHint = useAccountOffHint();
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const del = async (a: Account) => {
    const yes = await askConfirm({
      question: t('deleteConfirmQ'),
      confirmLabel: t('deleteYes'),
      cancelLabel: t('cancel'),
    });
    if (!yes) return;
    const r = await fetch(accountUrl(a.id), { method: 'DELETE' }).catch(() => null);
    if (r?.ok) reload();
  };
  return (
    <div className="ai-vgroups scn-vgroups scn-grid conn-grid conn-tiles">
      {rows.map((a) => {
        const custom = isCustomServiceCredType(a.type);
        const name = custom ? a.name : (serviceFor(a.type)?.name ?? fallbackServiceName(a.type));
        const handle = custom
          ? a.handle || t('customService')
          : (a.handle ?? '').trim() || (a.name ?? '').trim();
        const bad = a.health?.code ?? null;
        const sw = a.switch ?? null;
        const off = offHint(sw);
        const used = a.usedBy ?? [];
        const bots =
          used.length === 0
            ? t('tileBotsNone')
            : t('tileBots', {
                names:
                  used.slice(0, 3).join(', ') +
                  (used.length > 3 ? `, ${t('tileBotsMore', { n: used.length - 3 })}` : ''),
              });
        return (
          <div
            className="scn-card"
            key={a.id}
            role="button"
            tabIndex={0}
            onClick={() => openAccount(a)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') openAccount(a);
            }}
          >
            <ServiceLogo credType={a.type} iconNode={icons.get(a.type)} name={name} />
            <div className="scn-card-col">
              <div className="scn-card-name">
                {handle || name}
                <span
                  className={`scn-on-dot${bad ? ' bad' : ' on'}`}
                  title={bad ? t(`health.${bad}`) : t('stateOk')}
                >
                  {'•'}
                </span>
              </div>
              {/* Выключенный аккаунт (§3.4, §3.7) — вместо «Работает» подпись-причина. */}
              <div className={`scn-card-desc${bad ? ' bad' : ''}`}>
                {bad ? t(`health.${bad}`) : (off ?? t('stateOk'))}
              </div>
              <div className="scn-card-desc">{bots}</div>
              <div className="scn-card-desc">{t('tileDialogs', { n: a.dialogs ?? 0 })}</div>
              {a.connectedAt ? (
                <div className="scn-card-desc">
                  {t('tileSince', {
                    date: new Date(a.connectedAt).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    }),
                  })}
                </div>
              ) : null}
              <IgQueueNote queue={a.igQueue} />
            </div>
            <span className="scn-card-menu" onClick={stop} role="presentation">
              <PopMenu
                ariaLabel={t('cardMenuLabel')}
                triggerClassName="scn-card-dots"
                trigger={DOTS}
                sections={[
                  { items: [{ key: 'open', label: t('menuOpen') }] },
                  { items: [{ key: 'delete', label: t('deleteAccount'), danger: true }] },
                ]}
                onPick={(k) => {
                  if (k === 'open') openAccount(a);
                  else if (k === 'delete') void del(a);
                }}
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}

function subtitleOf(a: Account, serviceName: string): string {
  const name = (a.name ?? '').trim();
  const handle = (a.handle ?? '').trim();
  if (!name) return handle;
  if (!handle) return `«${name}»`;
  // Имя собрано нами: «<Сервис> <логин>» или просто сам логин — это не имя человека.
  const auto = name === `${serviceName} ${handle}` || name === handle;
  return auto ? handle : `«${name}» · ${handle}`;
}

// Значок сервиса (канон-плитка 40) переехал в connection-forms.tsx (ServiceLogo):
// его рисуют и этот список, и карточки каталога «Подключить» — здесь остался импорт.

/** Категория подключённого доступа: кураторская группа каталога (PICKER_GROUPS) или
 *  «Другое» — свои сервисы и типы вне групп (в т.ч. заведённые руками в редакторе). */
function connCategoryOf(credType: string): string {
  if (isCustomServiceCredType(credType)) return 'other';
  return PICKER_GROUPS.find((g) => g.types.includes(credType))?.key ?? 'other';
}

/** Заголовок карточки доступа — ОПОЗНАВАТЕЛЬ (владелец 29.08): логин бота, аккаунт
 *  Instagram, магазин кассы. Одна рука на плитки веба и ряды родной страницы. */
function useAccountTitle(): (a: Account) => string {
  const t = useTranslations('cabinet.connections');
  return (a) => a.handle || a.name || serviceFor(a.type)?.name || t('account');
}

/** СТРОКИ СПИСКА ПОДКЛЮЧЕНИЙ — ОДНА ВЫБОРКА ДЛЯ ТЕЛЕФОНА И КОЛОНКИ ТРЁХ СЛОЁВ (партия Н7,
 *  кадр iOS K10-connections): заголовок — логин как есть («@romabotrubot»), подпись — состояние
 *  («Работает» / слово поломки). Колонка (split-menu.tsx) рисует их своими пунктами. */
export interface ConnRow {
  id: string;
  type: string;
  title: string;
  hint: string;
  bad: boolean;
}
export function useConnectionRows(): { rows: ConnRow[]; ready: boolean } {
  const t = useTranslations('cabinet.connections');
  const offHint = useAccountOffHint();
  const { data } = usePersistentData<AccountsData>(ACCOUNTS_KEY, ACCOUNTS_URL, {
    accept: (j) => !!(j as { ok?: boolean })?.ok,
    ...accountsFast<AccountsData>(),
  });
  const accounts = data?.available ? data.accounts : [];
  const rows = accounts.map((a) => {
    const custom = isCustomServiceCredType(a.type);
    const name = custom ? a.name : (serviceFor(a.type)?.name ?? fallbackServiceName(a.type));
    const handle = custom
      ? a.handle || t('customService')
      : (a.handle ?? '').trim() || (a.name ?? '').trim();
    const bad = a.health?.code ?? null;
    const off = offHint(a.switch);
    return {
      id: a.id,
      type: a.type,
      title: handle || name,
      // Выключенный аккаунт (ТЗ ACCOUNTS-TOGGLE §3.4) — подпись-причина вместо «Работает».
      hint: bad ? t(`health.${bad}`) : (off ?? t('stateOk')),
      bad: !!bad,
    };
  });
  return { rows, ready: !!data };
}

/** ШТОРКИ РАЗДЕЛА — ОДНОЙ РУКОЙ: карточка доступа (модалка с ✓ в шапке) и каталог
 *  «Подключить». Ими живут и плитки веб-раздела, и ряды РОДНОЙ страницы «Подключений»:
 *  внешняя авторизация (OAuth) — всегда веб-флоу, второго пути подключения нет. */
function useConnectionSheets(reload: () => void): {
  openAccount: (a: Account) => void;
  openConnect: () => void;
} {
  const t = useTranslations('cabinet.connections');
  const drawer = useSlidePanel();
  const accountTitle = useAccountTitle();
  const openAccount = (a: Account) => {
    const submit: { current: () => void } = { current: () => {} };
    // Телефон в браузере (кадр iOS K12-connection-tg): имени в шапке нет — логин стоит
    // заголовком героя карточки; в шапке только кружки.
    const phone = readPhoneLook();
    drawer.open({
      title: phone ? '' : accountTitle(a),
      sheet: true,
      // Адрес карточки (ТЗ WEB-LOOK Э2.4): /app/connections/<id>; только в браузере.
      ...panelAddress(sectionUrl('connections', `acc:${a.id}`), sectionUrl('connections', '')),
      // КОНЕЧНЫЙ ОБЪЕКТ ИЗ СПИСКА — СТОПКОЙ (Н39, владелец 02.09.2026): лист снизу, экран
      // под ним уходит назад колодой, за ним фирменная подложка (§11.0 «стопка»).
      deck: true,
      // «‹» всегда — и корнем (прямой заход по адресу), и над/под другими листами (кадры iOS K12-connection-tg,
      // K12-connection-niche-pick; ТЗ WEB-CAUSES М83, доработка ФИНАЛ-2: признак не зависит от места в стопке).
      lead: 'back',
      actionLabel: t('saveAction'),
      // Круглая иконка вместо слова в кружке (владелец 2026-08-07).
      actionIcon: 'checkmark',
      onAction: () => submit.current(),
      body: <AccountDrawer account={a} reload={reload} submitRef={submit} />,
    });
  };
  // Каталог «Подключить» (ТЗ WEB-LOOK Э2.4–2.5): в браузере — вертикальный лист с адресом
  // /app/connections/new; в приложении — как было (webOnly/panelAddress пусты).
  const openConnect = () =>
    drawer.open({
      title: t('connectTitle'),
      ...webOnly({ sheet: true }),
      ...panelAddress(sectionUrl('connections', 'all'), sectionUrl('connections', '')),
      body: <ConnectPicker reload={reload} />,
    });
  return { openAccount, openConnect };
}

/**
 * РОДНАЯ СТРАНИЦА «ПОДКЛЮЧЕНИЯ» (docs/NATIVE-TABS-PLAN.md, волна 3 — аудит халявы).
 *
 * ВИД — КАНОН РОДНЫХ СТРАНИЦ (владелец 09.09.2026, ТЗ «Подключения» п.5–9; отменяет
 * прежнюю «сетку карточек один в один с вебом» от 01.09.2026):
 *   • ОБЫЧНЫЕ СТРОКИ В ПУЗЫРЕ, как у всех строк «Аккаунта»: маленькая плитка с логотипом
 *     сервиса, ОДНА строка названия (логин или имя), справа слово состояния («Работает»;
 *     «Ошибка» — сигнальным красным) и шеврон. Второй строки под названием нет, точки
 *     состояния нет — состояние говорит слово справа;
 *   • в шапке ТОЛЬКО круглый «+»: отбора по категориям и плитки-кнопки «Подключить»
 *     больше нет вовсе (единственный вход в подключение — «+»);
 *   • пусто — герой эталона с текстом и кнопкой «Подключить» (как у пустых «Рассылок»);
 *   • обрыв связи — честный повтор.
 *
 * `stack` — что открыто прямо сейчас: пока свод «Настроек» не открыт, страница в сеть не
 * ходит (кэш кабинета показывает, запросов не делает).
 */
export function useNativeConnectionsPage(
  hero: NativeAccountHero,
  stack: string[],
): {
  pages: NativeAccountPage[];
  tapRow: (rowKey: string) => boolean;
  applySet: (rowKey: string, value: string | boolean) => boolean;
} {
  const t = useTranslations('cabinet.connections');
  const tc = useTranslations('cabinet');
  const open = stack.includes('settings');
  // Доступы, каталог и логотипы — ОБЩИМ сбором (connection-forms.tsx, useConnectFeed):
  // тем же кормится стопка подключения из мастера — второй копии этого сбора нет.
  const { data, error, accounts, reload, catalog: cat, catalogError, logos } = useConnectFeed(open);
  const { openAccount } = useConnectionSheets(reload);
  const accountTitle = useAccountTitle();
  const offHint = useAccountOffHint();
  // Родные страницы глубины: каталог «Подключить», форма подключения и карточка
  // доступа (connection-forms.tsx — там же живут схема, запись и окна исходов).
  const deep = useNativeConnectPages({
    accounts,
    reload,
    stack,
    catalog: cat,
    catalogError,
    logos,
  });

  // ЦВЕТА — ЧИСЛА ВЕБА: сигнальный #e23570 (причина поломки под логином), серая роль --mut — запаска
  // плитке логотипа без своего цвета. Своих оттенков натив не считает и не изобретает.
  const badInk = readRoleHex('--accent-stroke', '#e23570');
  const tileOff = readRoleHex('--mut', '#8a8a8e');

  // СТРОКИ, А НЕ КАРТОЧКИ (ТЗ п.8): плитка логотипа слева, ОДНА строка названия —
  // опознаватель (логин бота, аккаунт Instagram, магазин кассы), справа шеврон.
  //
  // СОСТОЯНИЕ — ВТОРОЙ СТРОКОЙ ПОД ЛОГИНОМ (владелец 09.09.2026, волна 2, общее правило
  // П3): длинный логин спорил со словом справа и обрывался на «Работ…». Теперь это
  // обычная строка iOS «заголовок + подзаголовок + шеврон»: имя целиком, под ним серым
  // состояние, а у поломки — её причина теми же словами, что на веб-плитке, сигнальным
  // цветом. Значения справа у такой строки нет вовсе.
  const rows: NativeAccountField[] = accounts.map((a) => {
    const name = serviceNameOf(a);
    const bad = a.health?.code ?? null;
    const logo = logos[a.type];
    return {
      key: `conn:${a.id}`,
      kind: 'nav' as const,
      // Родная карточка доступа есть не у всех типов: мастера касс и почты и форма
      // «Своего сервиса» — по-прежнему веб-шторки (оговорка в connection-forms.tsx).
      ...(nativeConnectSupported(a.type) ? { page: CONN_PAGES.acc } : {}),
      title: accountTitle(a),
      // Выключенный аккаунт (ТЗ ACCOUNTS-TOGGLE §3.4) — подпись-причина; тумблер — в карточке.
      hint: bad ? t(`health.${bad}`) : (offHint(a.switch) ?? t('stateOk')),
      ...(bad ? { hintColor: badInk } : {}),
      ...(logo?.logo
        ? { logo: logo.logo }
        : { letter: logo?.letter ?? name.charAt(0).toUpperCase() }),
      logoColor: logo?.logoColor ?? tileOff,
    };
  });
  const connectDisabled = !data || data.state === 'none';

  // Обрыв связи на холодном старте (кэша нет) — не вечный скелетон, а честный повтор
  // (тот же LoadError, что в вебе): объяснение и кнопка.
  const broken = !data && !!error;
  const groups: NativeAccountGroup[] = broken
    ? [
        { key: 'conn-err', tone: 'hint', hint: tc('loadFailed'), fields: [] },
        {
          key: 'conn-retry',
          fields: [{ key: 'conn:retry', kind: 'button', title: tc('retry'), tone: 'accent' }],
        },
      ]
    : [
        {
          key: 'conns',
          // Пусто и данные ДОЕХАЛИ — честное слово под пузырём. Свежие сборки его не
          // показывают вовсе: у них пустота — герой с кнопкой (`empty` ниже), — а
          // старым это остаётся единственным ответом (запасной ход, запрет №8).
          ...(data && accounts.length === 0 ? { hint: t('emptyAll') } : {}),
          fields: rows,
        },
      ];
  // ПУСТО — ГЕРОЙ ЭТАЛОНА С КНОПКОЙ (ТЗ п.9): те же слова, что у сноски выше, и кнопка
  // «Подключить», ведущая в тот же каталог, что «+» в шапке.
  const empty =
    data && accounts.length === 0 && !broken && !connectDisabled
      ? {
          key: 'conn:add',
          page: CONN_PAGES.add,
          icon: hero.icon ?? 'link',
          title: hero.title,
          meta: t('emptyAll'),
          cta: t('connect'),
        }
      : null;

  const page: NativeAccountPage = {
    key: 'connections',
    title: hero.title,
    hero,
    // Список ещё едет — пузырь ждёт пустой, а не показывает «пока пусто».
    loading: !data && !error,
    ...(empty ? { empty } : {}),
    // В ШАПКЕ ТОЛЬКО «+» (ТЗ п.6): кнопка-воронка отбора по категориям убрана совсем —
    // подключение живёт одним входом.
    actions: [
      {
        key: 'conn:add',
        icon: 'plus',
        label: t('connect'),
        page: CONN_PAGES.add,
        disabled: connectDisabled,
      },
    ],
    groups,
  };

  const tapRow = (rowKey: string): boolean => {
    if (rowKey === 'conn:add') {
      // Каталог натив уже открыл сам: страницу называют и «+» в шапке, и кнопка героя
      // пустого состояния — вебу остаётся только запомнить стопку.
      return false;
    }
    if (rowKey === 'conn:retry') {
      reload();
      return true;
    }
    const m = /^conn:(.+)$/.exec(rowKey);
    if (m && !rowKey.startsWith('conn:svc:')) {
      const acc = accounts.find((a) => a.id === m[1]);
      if (acc) {
        // Тип с родной карточкой — её уже открыл натив, вебу остаётся налить содержимое;
        // мастера касс и почты по-прежнему открываются прежней веб-шторкой.
        if (nativeConnectSupported(acc.type)) {
          deep.showAccount(acc.id);
          return false;
        }
        openAccount(acc);
        return true;
      }
    }
    return deep.tapRow(rowKey);
  };

  const applySet = (rowKey: string, value: string | boolean): boolean =>
    deep.applySet(rowKey, value);

  return { pages: [page, ...deep.pages], tapRow, applySet };
}

export function Connections({
  actionRef,
  pick = null,
  onPick,
}: {
  /** Шторка «Данных» (data-menu.tsx): сюда кладётся обработчик «+» (открыть подключение). */
  actionRef?: SectionActionRef;
  /** Выбор во второй колонке десктопа (владелец 2026-08-01). null — колонки нет
   *  (узкий экран): категории — кружком-фильтром в шапке страницы (2026-08-04). */
  pick?: string | null;
  onPick?: (p: ConnectionsPick) => void;
} = {}) {
  const t = useTranslations('cabinet.connections');
  const tsec = useTranslations('cabinet.sections');
  const drawer = useSlidePanel();
  // QUICK-START §11.37 п.6 (владелец 31.10): вход Instagram в той же вкладке вернул «аккаунт занят» (`?connect=busy&provider&owner`
  // от колбэка, ключ согласия — в hold-cookie): вопрос герой-листом снизу; «Подключить здесь» — форма confirm (POST),
  // дальше колбэк возвращает сюда же; «Отмена» — ничего не сделано, ключ протухнет сам.
  const busyAsked = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined' || busyAsked.current) return;
    const q = new URLSearchParams(window.location.search);
    if (q.get('connect') !== 'busy') return;
    busyAsked.current = true;
    const provider = /^[a-z]+$/.test(q.get('provider') ?? '') ? String(q.get('provider')) : 'instagram';
    const owner = q.get('owner') || '';
    const clean = new URL(window.location.href);
    for (const key of ['connect', 'provider', 'owner']) clean.searchParams.delete(key);
    window.history.replaceState(window.history.state, '', clean.toString());
    openHeroAsk(drawer, {
      icon: HERO_ICONS.persons,
      title: t('takenByHead'),
      text: t('takenBy', { owner: owner || t('takenByUnknown') }),
      actions: [
        {
          key: 'takeover',
          label: t('takeoverAction'),
          tone: 'primary',
          onPick: () => {
            const f = document.createElement('form');
            f.method = 'post';
            f.action = `/api/connect/${provider}/confirm`;
            document.body.appendChild(f);
            f.submit();
          },
        },
      ],
      closeLabel: t('takeoverCancel'),
    });
  }, [drawer, t]);
  // ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 К18, кадр iOS K10-connections): герой со значком-
  // цепью и «Подключения» по центру, ОДНА карточка строк «значок · @логин / Работает ›»,
  // «+» — в шапке листа; фильтра и карточки «Подключить» нет.
  const phoneWeb = usePhoneLook();
  const layoutMode = useLayoutMode();
  // Активна ли панель стека: только она отдаёт кнопки в шапку (портал PageAction).
  const paneActive = useContext(PaneActiveContext);
  // Страница стоит под шапкой поля «Настроек» (<PaneHead> — заголовок «Подключения» уже есть):
  // «+» уходит в её строку, своего второго заголовка не рисуем (Д1.2 п.3, Закон 5 п.7).
  const paneHeadSlot = useContext(PaneActSlotContext);
  // Категория на узком экране — локальный выбор страницы (колонки и адресов там нет).
  const [mobCat, setMobCat] = useState('');
  // Персистентный кэш + скелетон — единый хук. reload ревалидирует (без сброса в скелетон).
  const { data, error, reload } = usePersistentData<AccountsData>(ACCOUNTS_KEY, ACCOUNTS_URL, {
    accept: (j) => !!(j as { ok?: boolean })?.ok,
    ...accountsFast<AccountsData>(),
  });
  // Каталог сервисов — ТОТ ЖЕ кэш, что у пикера «+ Подключить» (один ключ на весь
  // раздел): здесь он даёт credType→узел (цветной логотип из N8N_ICONS), а его загрузка
  // при входе в раздел заодно ПРОГРЕВАЕТ каталог для пикера — тот открывается мгновенно.
  const { data: cat } = usePersistentData<{ services?: { credType: string; iconNode?: string }[] }>(
    'cabinet:accounts:catalog',
    '/api/cabinet/accounts/catalog',
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  const credIconNode = new Map((cat?.services ?? []).map((s) => [s.credType, s.iconNode]));

  // ПРЕДЗАГРУЗКА (владелец 2026-08-03, п.1): при входе в раздел греем данные каждого
  // подключённого (карточка + схема его формы + статус входа) — тем же механизмом
  // usePersistentData/prefetchPersistent, что и остальной кабинет. Открытие карточки —
  // мгновенно из кэша, свежее доезжает тихо фоном.
  const locale = useLocale();
  const accountsList = data?.accounts;
  useEffect(() => {
    if (!accountsList) return;
    for (const a of accountsList) warmAccountData(a.id, a.type, locale);
  }, [accountsList, locale]);
  // Схемы форм — для ВСЕХ сервисов каталога, не только подключённых (владелец
  // 2026-08-03, п.3: «Подключить Email» у человека без почты открывался скелетоном).
  // Сервисов немного, запрос лёгкий; тот же штатный прогрев prefetchPersistent.
  const catServices = cat?.services;
  useEffect(() => {
    if (!catServices) return;
    for (const s of catServices) warmCredSchema(s.credType, locale);
  }, [catServices, locale]);

  // Карточка доступа — МОДАЛКОЙ (владелец 2026-08-03, по образцу товаров): нижний лист,
  // внутри та же форма правки со всеми свойствами, сохранение — круглой ✓ в шапке
  // (submitRef), кнопки «Сохранить» в теле нет. Каталог «Подключить» — прежней шторкой.
  // Обе руки — общие с РОДНОЙ страницей раздела (useConnectionSheets выше).
  const { openAccount, openConnect } = useConnectionSheets(reload);
  const accountTitle = useAccountTitle();
  const offHint = useAccountOffHint();
  const askNiche = useNicheAsk();
  // «+» шторки «Данных» зовёт то же подключение (ref живёт у шторки, не состояние).
  if (actionRef) actionRef.current = openConnect;
  // Форма подключения из каталога — ТОЖЕ модалкой (владелец 2026-08-03); формы не
  // перекраиваются, у них остаётся своя кнопка «Подключить» (внизу есть OAuth-ветки).
  const openService = (credType: string) => {
    if (credType === 'custom') {
      drawer.open({
        title: t('customService'),
        sheet: true,
        body: (
          <CustomServiceForm
            reload={reload}
            onDone={() => {
              reload();
              drawer.close();
            }}
          />
        ),
      });
      return;
    }
    drawer.open({
      title: t('connectServiceTitle', {
        name: serviceFor(credType)?.name ?? fallbackServiceName(credType),
      }),
      sheet: true,
      body: (
        <ConnectCredTypeForm
          credType={credType}
          onDone={(id) => {
            reload();
            drawer.close();
            // Подключили КАНАЛ — сразу спрашиваем про него самого (О5.2 п.3): ниша, что
            // предлагает и пол. Прочим сервисам вопрос не задаётся — рука молчит сама.
            askNiche(credType, id);
          }}
        />
      ),
    });
  };

  // Адресные «карточки» (владелец 2026-08-03, по образцу товаров): /app/connections/<id>,
  // /service/<credType> и /custom открывают модалку поверх плиток и возвращают адрес к
  // списку (у шторки нет колбэка закрытия — иначе «выбранный» путь повисал бы без окна).
  // Форма из каталога возвращает адрес каталогу — модалка открыта над ним.
  // АДРЕС У ПАНЕЛИ (ТЗ WEB-LOOK Э2.4): в браузере окно держит свой адрес, пока открыто, —
  // возврата адреса к списку нет (его вернёт закрытие через closeHref/историю); без колонки
  // (телефон, два слоя) выбор берётся из самого адреса — так прямой заход открывает окно.
  const addrPick = useAddressPick('connections', pick);
  useEffect(() => {
    if (!addrPick || addrPick.startsWith('cat:')) return;
    if (isOwnPanelHref(sectionUrl('connections', addrPick))) return;
    if (addrPick === 'all') {
      // В трёх слоях каталог стоит в поле (ниже); панелью — только без колонки.
      // Ширину читаем синхронно: в кадре гидрации pick ещё null и на десктопе.
      if (pick !== null || readLayoutMode() === 'three') return;
      const tm = window.setTimeout(openConnect, 0);
      return () => window.clearTimeout(tm);
    }
    if (addrPick === 'custom' || addrPick.startsWith('svc:')) {
      // Транзитные адреса формы сервиса — как были: только из колонки трёх слоев.
      if (pick === null) return;
      openService(addrPick === 'custom' ? 'custom' : addrPick.slice(4));
      onPick?.('all');
      return;
    }
    const acc = addrPick.startsWith('acc:')
      ? ((data?.accounts ?? []).find((a) => a.id === addrPick.slice(4)) ?? null)
      : null;
    if (acc) {
      // drawer.open зовёт flushSync — из эффекта только отдельной задачей (капкан Э2.1).
      const tm = window.setTimeout(() => openAccount(acc), 0);
      return () => window.clearTimeout(tm);
    }
    // pick === null — в deps: первый кадр гидрации отдаёт «без колонки», и отложенное открытие
    // каталога панелью обязано отмениться, когда колонка появится (в трёх слоях он в поле).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openAccount/openService/onPick пересоздаются каждый рендер; входы — адрес и приехавший список
  }, [addrPick, data, pick === null]);

  // Вторая колонка десктопа: выбранный КАТАЛОГ занимает всё правое поле — плитки
  // подключённого в это время не рисуются. Выбор сервиса открывает форму модалкой
  // (через транзитный адрес svc:/custom — ссылка на форму копируется).
  if (pick === 'all') {
    return (
      <>
        <div className="split-pane-t">{t('splitCreate')}</div>
        {/* 'custom' — плитка «Свой сервис»: своя форма, а не форма по схеме кред-типа. */}
        <ConnectPicker
          reload={reload}
          onPickService={(credType) =>
            onPick?.(credType === 'custom' ? 'custom' : `svc:${credType}`)
          }
        />
      </>
    );
  }

  const accounts = data?.available ? data.accounts : [];
  // Категория: на узком экране — выбор страницы (mobCat), на широком — из адреса.
  const catKey = pick === null ? mobCat : pick.startsWith('cat:') ? pick.slice(4) : '';
  const shownAccounts =
    catKey === '' ? accounts : accounts.filter((a) => connCategoryOf(a.type) === catKey);
  // Тап по адресному «подключению не найдено» бессмыслен — честное сообщение (п.6):
  // список загружен, а доступа по ссылке в нём нет (удалили/чужая ссылка).
  const accGone = !!(
    pick &&
    pick.startsWith('acc:') &&
    data &&
    !(data.accounts ?? []).some((a) => a.id === pick.slice(4))
  );

  const connectDisabled = !data || data.state === 'none';
  // «Подключить»: на широком экране — каталог в правом поле, на узком — прежняя шторка.
  const goConnect = pick === null ? openConnect : () => onPick?.('all');

  // Плитки подключённого — дизайн карточек «Скриптов» (те же .scn-grid/.scn-card):
  // плитка-значок сервиса 40, имя с точкой здоровья/занятости, опознаватель подписью.
  // Последней плиткой — «Подключить» (ведёт в каталог).
  const tiles =
    !data && error ? (
      // Обрыв сети на холодном старте (кэша нет) — не вечный скелетон, а повтор (Ф28).
      <div className="ai-vgroups" style={{ marginBottom: 26 }}>
        <div className="ai-vgroup">
          <LoadError retry={reload} />
        </div>
      </div>
    ) : !data ? (
      <div
        className="ai-vgroups scn-vgroups scn-grid conn-grid sk-hold"
        style={{ marginBottom: 26 }}
      >
        {[0, 1, 2].map((i) => (
          <div className="scn-card" key={i}>
            <Sk w={40} h={40} r="round" />
            <div className="scn-card-col" style={{ gap: 7 }}>
              <Sk w={140} h={13} />
              <Sk w={90} h={11} />
            </div>
          </div>
        ))}
      </div>
    ) : accGone ? (
      <DEmpty>{t('accGone')}</DEmpty>
    ) : shownAccounts.length === 0 ? (
      // Честные пустые состояния (владелец 2026-08-03, п.6): в категории пусто и совсем
      // пусто — разные тексты. Кнопки тут больше нет (владелец 2026-08-07): подключение
      // живёт круглым «+» в строке заголовка, второй вход на том же экране — ошибка.
      <DEmpty>{catKey === '' || accounts.length === 0 ? t('emptyAll') : t('catEmpty')}</DEmpty>
    ) : (
      <div className="ai-vgroups scn-vgroups scn-grid conn-grid" style={{ marginBottom: 26 }}>
        {/* Наших эндпоинтов (Scriptera AI / Scriptera Run) в списке НЕТ (владелец
            2026-08-01: «есть и есть, чего их писать») — они и так работают в
            сценариях. Раздел показывает только доступы клиента. */}
        {shownAccounts.map((a) => {
          // «Свой сервис»: заголовком — ИМЯ сервиса, которое дал клиент (тип-generic
          // человеку ничего не говорит), подписью — хост адреса или слово «Свой сервис».
          const custom = isCustomServiceCredType(a.type);
          const name = custom ? a.name : (serviceFor(a.type)?.name ?? fallbackServiceName(a.type));
          // Подпись карточки — то, ЧТО именно подключено: логин бота «@my_bot»,
          // аккаунт Instagram, адрес портала, идентификатор магазина. Не узнали
          // (доступ заведён руками в редакторе — ключа у нас нет) — честно
          // показываем название доступа, как было раньше.
          const used = a.usedBy?.length ?? 0;
          // Сторож здоровья (К36): подключение умерло — говорим об этом ПЕРВЫМ
          // делом, красной точкой и словами. Клиент не должен узнавать о смерти
          // бота от своих покупателей.
          const bad = a.health?.code ?? null;
          return (
            <div
              className="scn-card"
              key={a.id}
              role="button"
              tabIndex={0}
              onClick={() => openAccount(a)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openAccount(a);
              }}
            >
              <ServiceLogo credType={a.type} iconNode={credIconNode.get(a.type)} name={name} />
              <div className="scn-card-col">
                <div className="scn-card-name">
                  {name}
                  {/* Точка статуса со словом-подсказкой (канон рядов, Р6). ЦВЕТ — ЗДОРОВЬЕ
                      ДОСТУПА (владелец 03.09.2026: «только добавил токен — чё он не зелёный»):
                      зелёная — доступ проверен и работает, красная — сломан. Занятость в ботах
                      точкой больше не показываем — она в слове-подсказке и на карточке. */}
                  <span
                    className={`scn-on-dot${bad ? ' bad' : ' on'}`}
                    title={
                      bad
                        ? t(`health.${bad}`)
                        : used > 0
                          ? t('dotUsed', { count: used })
                          : t('dotFree')
                    }
                  >
                    {'•'}
                  </span>
                </div>
                <div className={`scn-card-desc${bad ? ' bad' : ''}`}>
                  {bad
                    ? t(`health.${bad}`)
                    : custom
                      ? a.handle || t('customService')
                      : subtitleOf(a, name)}
                </div>
                {/* ОЧЕРЕДЬ ОТВЕТОВ ИНСТАГРАМА (SCALE-PLAN Б5) — та же подпись, что на
                    карточке бота: ответы на комментарии уходят по порядку. */}
                <IgQueueNote queue={a.igQueue} />
              </div>
            </div>
          );
        })}
        {/* Кнопка-плитка «Подключить» — В КОНЦЕ сетки (владелец 2026-08-03): та же
            карточка, значок «+» в кружке цвета акцента, ведёт в каталог. */}
        <div
          className="scn-card"
          role="button"
          tabIndex={0}
          aria-disabled={connectDisabled || undefined}
          onClick={() => {
            if (!connectDisabled) goConnect();
          }}
          onKeyDown={(e) => {
            if (!connectDisabled && (e.key === 'Enter' || e.key === ' ')) goConnect();
          }}
        >
          <div className="ic brand svc-circle" style={{ color: 'var(--accent-d)' }} aria-hidden>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </div>
          <div className="scn-card-col">
            <div className="scn-card-name">{t('connect')}</div>
          </div>
        </div>
      </div>
    );

  // ── Узкий экран (колонки нет): категории — кружком-фильтром в шапке ────────────────
  // Айфон-шапка узкого веба (владелец 2026-08-04): отбор — круглой кнопкой фильтра
  // СПРАВА в шапке страницы (портал PageAction в #cab-topact), ПЕРЕД «+»; выпадашки
  // категорий в теле нет. По тапу выезжает НИЖНИЙ ЛИСТ фильтров (владелец 2026-08-05,
  // IMG_1278), общий механизм FilterButton. В шторке «Данных» портал заглушён
  // (PaneActiveContext=false): кружок остаётся в теле шторки, прижатый вправо, лист
  // выезжает ПОВЕРХ слоя стопкой (§11), а кнопка шапки слоя — прямой «+».
  // Д7 (владелец 20.08): секции НЕ ждут списка доступов — категории статичны, а кнопка,
  // появлявшаяся «когда данные доехали», на проде выскакивала с опозданием в секунды
  // (список идёт с сервера ботов) и человек её просто не заставал.
  const fltSections = [
    {
      title: t('fltCat'),
      items: [
        { key: 'all', label: t('catAll'), on: mobCat === '' },
        ...PICKER_GROUPS.map((g) => ({ key: g.key, label: t(g.labelKey), on: mobCat === g.key })),
        { key: 'other', label: t('catOther'), on: mobCat === 'other' },
      ],
    },
  ];
  const fltPick = (k: string) => setMobCat(k === 'all' ? '' : k);
  const fltBtn = (
    <FilterButton
      sections={fltSections}
      onPick={fltPick}
      active={mobCat !== ''}
      ariaLabel={t('fltCat')}
    />
  );
  // ДВА СЛОЯ — ТОТ ЖЕ ЭКРАН ТЕЛЕФОНА (партия Н7, «Фундамент дизайна» п.4): строки «значок ·
  // @логин / Работает ›» и один «+» в шапке страницы; кружка-фильтра категорий нет (на
  // телефоне его нет — подключать можно только Telegram и Instagram). Герой со значком-цепью
  // — только у листа телефона: в двух слоях страницу подписывает шапка раздела.
  // ЗАКОН 4 (владелец 04.10.2026): на любой ширине в поле — телефонный список подключений,
  // карточка подключения — панелью-стопкой 460 (своего разворота у раздела нет).
  if (pick === null) {
    const rows = data ? shownAccounts : [];
    return (
      <>
        {phoneWeb ? (
          <SettingsHero icon={<IconConnections />} title={tsec('connections')} />
        ) : paneActive && (layoutMode !== 'three' || paneHeadSlot) ? (
          <PageAction>
            <PageAddButton label={t('connect')} onClick={openConnect} disabled={connectDisabled} />
          </PageAction>
        ) : (
          // Поле трёх слоёв без шапки «Настроек» (прямой адрес /app/connections; Закон 4): заголовок страницы и «+», как у «Ботов» (Н4).
          // Под <PaneHead> «Настроек» сюда не попадаем — имя страницы стоит один раз (Д1.2 п.3).
          <div className="split-pane-t pane-head">
            <span className="pane-head-t">{tsec('connections')}</span>
            <span className="pane-head-act">
              <PageAddButton
                label={t('connect')}
                onClick={openConnect}
                disabled={connectDisabled}
              />
            </span>
          </div>
        )}
        {!data && error ? (
          <LoadError retry={reload} />
        ) : data && rows.length === 0 ? (
          <DEmpty>{t('emptyAll')}</DEmpty>
        ) : !phoneWeb ? (
          // Широкий экран (владелец 11.10.2026, TARIFF-PAGE-TZ П6): плитки как у ботов с подробностями.
          <ConnTiles rows={rows} icons={credIconNode} openAccount={openAccount} reload={reload} />
        ) : (
          <KListGroup className="conn-rows">
            {rows.map((a) => {
              const custom = isCustomServiceCredType(a.type);
              const name = custom
                ? a.name
                : (serviceFor(a.type)?.name ?? fallbackServiceName(a.type));
              const bad = a.health?.code ?? null;
              // Заголовок — логин как есть (кадр K10: «@romabotrubot»), без кавычек имени.
              const handle = custom
                ? a.handle || t('customService')
                : (a.handle ?? '').trim() || (a.name ?? '').trim();
              return (
                <KListRow
                  key={a.id}
                  icon={
                    <ServiceLogo
                      credType={a.type}
                      iconNode={credIconNode.get(a.type)}
                      name={name}
                    />
                  }
                  title={handle || name}
                  hint={bad ? t(`health.${bad}`) : (offHint(a.switch) ?? t('stateOk'))}
                  {...(bad ? { tone: 'danger' as const } : {})}
                  chevron
                  onClick={() => openAccount(a)}
                />
              );
            })}
          </KListGroup>
        )}
      </>
    );
  }
  if (pick === null) {
    return (
      <>
        {paneActive ? (
          <PageAction>
            {fltBtn}
            {/* Слова на кнопке нет (владелец 2026-08-07): у всех страниц один вид —
                круглый «+» с акцентным глифом, подпись только для наведения и VoiceOver. */}
            <PageAddButton label={t('connect')} onClick={openConnect} disabled={connectDisabled} />
          </PageAction>
        ) : (
          <FilterRow flush>{fltBtn}</FilterRow>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{tiles}</div>
      </>
    );
  }

  // ── Три слоя (партия Н7): список подключений — в колонке, поле — СТРАНИЦА выбранного
  // подключения (без выбора — первого, как «Чаты» открывают первую переписку). Нечего
  // открыть (ещё грузится, пусто, ссылка на удалённое) — честное состояние под заголовком.
  // Удалённое (или чужая ссылка) — тоже первое: страница после удаления не висит пустой.
  const fieldAcc =
    (pick.startsWith('acc:') ? accounts.find((a) => a.id === pick.slice(4)) : undefined) ??
    accounts[0] ??
    null;
  // «+» — справа в шапке поля, как «+» «Ботов» (Н4): в шапке колонки ширины 221 он
  // обрезал бы заголовок «Подключения»; ведёт в каталог «Подключить» в поле.
  const addBtn = (
    <PageAddButton label={t('connect')} onClick={goConnect} disabled={connectDisabled} />
  );
  if (!fieldAcc) {
    return (
      <>
        <div className="split-pane-t pane-head">
          <span className="pane-head-t">{tsec('connections')}</span>
          <span className="pane-head-act">{addBtn}</span>
        </div>
        {tiles}
      </>
    );
  }
  return (
    <ConnectionPane
      key={fieldAcc.id}
      account={fieldAcc}
      title={accountTitle(fieldAcc)}
      add={addBtn}
      reload={reload}
      // После сохранения и удаления страница остаётся на месте и только освежает список:
      // удалённое пропадёт из колонки, а поле само встанет на первое подключение.
      onGone={reload}
    />
  );
}

/** СТРАНИЦА ПОДКЛЮЧЕНИЯ В ПОЛЕ ТРЁХ СЛОЁВ (партия Н7): то же тело, что у карточки-стопки
 *  (AccountDrawer, кадр iOS K12-connection-ig), шапка — заголовок слева, ✓ сохранения справа
 *  (на телефоне ✓ стоит в шапке листа). Ширина тела — `.split-acc` (520, как формы колонки). */
function ConnectionPane({
  account,
  title,
  add,
  reload,
  onGone,
}: {
  account: Account;
  title: string;
  /** «+» раздела — первым кружком шапки, перед ✓. */
  add: React.ReactNode;
  reload: () => void;
  onGone: () => void;
}) {
  const t = useTranslations('cabinet.connections');
  const submit = useRef<() => void>(() => {});
  return (
    <>
      <div className="split-pane-t pane-head">
        <span className="pane-head-t">{title}</span>
        <span className="pane-head-act">
          {add}
          <KButton
            className="page-add"
            title={t('saveAction')}
            aria-label={t('saveAction')}
            onClick={() => submit.current()}
            icon={
              <svg
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
            }
          />
        </span>
      </div>
      <div className="split-acc">
        <AccountDrawer account={account} reload={reload} submitRef={submit} onDone={onGone} />
      </div>
    </>
  );
}

/**
 * ПЕРЕХВАТ АДРЕСА `/app/connections` В ПРИЛОЖЕНИИ (волна 3, реестр веб-остатков).
 *
 * На «Подключения» вели ТРИ дороги мимо родного экрана — мастер шаблона («Подключите
 * телеграм-бота»), родная «Аналитика» (отказ Instagram) и тап по пушу «связь
 * сломалась»; все три звали веб-страницу. Теперь в свежей сборке адрес открывает
 * РОДНУЮ страницу свода «Настроек» (стопку ведёт веб — своего адреса у свода нет), а
 * сам адрес возвращается разделу «Аккаунт». Вне приложения и в старых сборках ничего
 * не меняется: страница рисуется как прежде.
 */
export function NativeConnectionsGate() {
  const router = useRouter();
  useEffect(() => {
    if (!openNativeSettingsPage(['settings', 'connections'])) return;
    router.replace('/app/account');
  }, [router]);
  return null;
}
